import { gql } from "@apollo/client";
import Form from "components/common/form/Form";
import Input from "components/common/form/inputs/Input";
import FormActions from "components/common/form/partials/FormActions";
import { graph } from "config/axios-context";
import { useState } from "react";
import { IdName, IdNameType } from "types/common-types";
import { SaveFruitMutation, SaveFruitMutationVariables } from "types/graphql";
import { useContext } from "react";	
import { getAppContext } from 'config/AppContext';
import { uuid } from "config/app-constants";
import Button from "components/common/button/Button";
import Translate from "components/common/Translate";
import { faTrash } from "@fortawesome/free-solid-svg-icons";
import { t } from "i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import If from "components/common/If";
import { removeAt } from "utilities/app-utilities";

export type FruitFromType = IdName & {
    categories: IdNameType<string | number, string>[],
    groups: IdNameType<string | number, string>[]
}

type FruitFormPropsType = {
    name: string,
    onSubmit: (item: IdName) => void,
    model?: FruitFromType
}

const fruitMutation = gql`
mutation SaveFruit($model: FruitInput!) {
    fruit(model: $model) {
      id,
      name
    }
  }
`

const FruitForm = (props: FruitFormPropsType) => {
    const context = getAppContext(useContext);
    const [model, setModel] = useState<FruitFromType>({
        id: props.model?.id || 0,
        name: props.model?.name || '',
        categories: props.model?.categories?.length ? props.model?.categories : [{
            id: uuid(),
            name: ''
        }],
        groups: props.model?.groups.length ? props.model?.groups : [{
            id: uuid(),
            name: ''
        }]
    });

    const [disableInputs, setDisableInputs] = useState<boolean>(false);

    const removeItem = (property: 'groups' | 'categories', index: number) => {
        model[property] = removeAt(model[property], index);
        setModel({...model});
    }

    const onSubmit = async () => {
        setDisableInputs(true);

        try {
            const response = await graph<SaveFruitMutation, SaveFruitMutationVariables>({
                query: fruitMutation,
                variables: {
                    model: {
                        id: model.id,
                        name: model.name,
                        categories: model.categories.map(c => {
                            return {
                                id: Number(c.id) || 0,
                                name: c.name,
                                fruitId: model.id
                            }
                        }),
                        groups: model.groups.map(c => {
                            return {
                                id: Number(c.id) || 0,
                                name: c.name,
                                fruitId: model.id
                            }
                        })
                    }
                }
            });
            
            if(response.errors) throw response.errors;

            props.onSubmit(response.data!.fruit as IdName);
        } catch (error: any) {
            context.notify('error', 'error.save', Array.isArray(error) ? error[0].message : error.message);
            throw error;
        } finally {
            setDisableInputs(false);
        }
    }

    return (
        <Form name={props.name} onSubmit={onSubmit}>
            <fieldset>
                <Input
                    required
                    label="fields.name.name"
                    minLength={2}
                    maxLength={50}
                    value={model.name}
                    onChange={v => setModel({ ...model, name: v || '' })}
                />
            </fieldset>
            <fieldset>
                {model.categories.map((category, index) => {
                    return (
                        <div key={`category-${category.id}`} className="flex">
                            <Input
                                required
                                label="fields.category.name"
                                minLength={2}
                                maxLength={50}
                                value={model.categories[index].name}
                                onChange={v => {
                                    model.categories[index].name = v || '';
                                    setModel({...model});
                                }}
                            />
                            <If condition={model.categories.length > 1}>
                                <Button type="button"  onClick={e => removeItem('categories', index)} title={t('button.remove')} className="text-danger mt-4" theme="link">
                                    <FontAwesomeIcon icon={faTrash} />
                                </Button>
                            </If>
                        </div>
                    );
                })}

                <Button type="button" title="add-more" onClick={() => {
                    model.categories.push({id: uuid(), name: ''});
                    setModel({...model});
                }}>
                    <Translate i18nKey="button.add-more" />
                </Button>
            </fieldset>
            <fieldset>
                {model.groups.map((group, index) => {
                    return (
                        <div key={`group-${group.id}`} className="flex">
                            <Input
                                required
                                label="fields.group.name"
                                minLength={2}
                                maxLength={50}
                                value={model.groups[index].name}
                                onChange={v => {
                                    model.groups[index].name = v || '';
                                    setModel({...model});
                                }}
                            />
                            <If condition={model.groups.length > 1}>
                                <Button type="button" onClick={() => removeItem('groups', index)} title={t('button.remove')} className="text-danger mt-4" theme="link">
                                    <FontAwesomeIcon icon={faTrash} />
                                </Button>
                            </If>
                        </div>
                    );
                })}
                <Button type="button" title="add-more" onClick={() => {
                    model.groups.push({id: uuid(), name: ''});
                    setModel({...model});
                }}>
                    <Translate i18nKey="button.add-more" />
                </Button>
            </fieldset>
            <FormActions disableInputs={disableInputs} />
        </Form>
    );
};

export default FruitForm;
